import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography,
  Box, Stack, TextField, FormControl, InputLabel, Select, MenuItem, Button,
  Paper, InputAdornment, alpha, useTheme, CircularProgress, Chip,
} from '@mui/material';
import { Close, Payments, ReceiptLong } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import finePaymentService from '../../services/finePaymentService';
import { LetterV2, FinePaymentMethod } from '../../types/letters';
import { PAYMENT_METHOD_OPTIONS } from '../../utils/paymentMethods';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { useAuth } from '../../context/AuthContext';
import { useReceipts } from '../finance/useReceipts';
import BusyIcon from '../common/BusyIcon';

interface Props {
  open: boolean;
  onClose: () => void;
  letter: LetterV2 | null;
  onRecorded?: () => void;
}

const toDateInputValue = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Modal for recording a payment against a fine letter. Auto-defaults the
 * amount to the outstanding balance so a "pay in full" is one click; the user
 * can edit downward for a partial payment.
 *
 * Theme-consistent: Paper-bordered call-out for the outstanding balance,
 * standard Dialog header + actions, error-tone for the balance number.
 */
/** The toast's "Receipt" button — spins until the receipt is downloaded. */
const ReceiptAction: React.FC<{ onDownload: () => Promise<void> }> = ({ onDownload }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onDownload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button color="inherit" size="small" startIcon={<BusyIcon busy={busy} size={14}><ReceiptLong fontSize="small" /></BusyIcon>} onClick={run}>
      {busy ? 'Preparing…' : 'Receipt'}
    </Button>
  );
};

const RecordPaymentDialog: React.FC<Props> = ({ open, onClose, letter, onRecorded }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const money = useCurrencyFormat();
  const { downloadFineReceipt } = useReceipts();
  const { currency } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const outstanding = useMemo(() => {
    if (!letter || letter.amount == null) return 0;
    return Math.max(0, (letter.amount || 0) - (letter.paidAmount || 0) - (letter.waivedAmount || 0));
  }, [letter]);

  const formik = useFormik({
    initialValues: {
      amount: outstanding ? String(outstanding) : '',
      method: 'cash' as FinePaymentMethod,
      collectedAt: toDateInputValue(new Date()),
      referenceNumber: '',
      notes: '',
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      amount: yup.number()
        .typeError('Amount must be a number')
        .positive('Amount must be positive')
        .max(outstanding, `Amount cannot exceed ${money(outstanding)}`)
        .required('Amount is required'),
      method: yup.string().required('Method is required'),
      collectedAt: yup.string().required('Date is required'),
    }),
    onSubmit: async (values) => {
      if (!letter) return;
      setSubmitting(true);
      try {
        const payment = await finePaymentService.recordPaymentForLetter(letter._id, {
          amount: Number(values.amount),
          method: values.method,
          collectedAt: new Date(values.collectedAt).toISOString(),
          referenceNumber: values.referenceNumber.trim() || undefined,
          notes: values.notes.trim() || undefined,
        });
        enqueueSnackbar(`Payment recorded${payment.receiptNumber ? ` — receipt ${payment.receiptNumber}` : ''}`, {
          variant: 'success',
          autoHideDuration: 8000,
          action: () => <ReceiptAction onDownload={() => downloadFineReceipt(payment)} />,
        });
        onRecorded?.();
        handleClose();
      } catch (err: any) {
        enqueueSnackbar(err.message || 'Failed to record payment', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    if (!open) formik.resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    formik.resetForm();
    onClose();
  };

  if (!letter) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{
            width: 32, height: 32, borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.success.main,
          }}>
            <Payments fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>Record Payment</Typography>
            <Typography variant="caption" color="text.secondary">
              {letter.categoryId?.label} · {letter.title || letter.reason}
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={handleClose} disabled={submitting}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Paper elevation={0} sx={{
          p: 2, mb: 2.5, borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          bgcolor: alpha(theme.palette.error.main, 0.04),
          textAlign: 'center',
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Outstanding balance
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ color: theme.palette.error.main, mt: 0.5 }}>
            {money(outstanding)}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
            <Chip label={`Total ${money(letter.amount || 0)}`} size="small" variant="outlined" />
            <Chip label={`Paid ${money(letter.paidAmount || 0)}`} size="small" variant="outlined" />
            {(letter.waivedAmount || 0) > 0 && (
              <Chip label={`Waived ${money(letter.waivedAmount || 0)}`} size="small" variant="outlined" />
            )}
          </Stack>
        </Paper>

        <form id="record-payment-form" onSubmit={formik.handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              name="amount"
              label="Amount"
              type="number"
              inputProps={{ min: 0, max: outstanding, step: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
              value={formik.values.amount}
              onChange={formik.handleChange}
              error={formik.touched.amount && Boolean(formik.errors.amount)}
              helperText={formik.touched.amount && (formik.errors.amount as string)}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Method</InputLabel>
              <Select
                name="method"
                value={formik.values.method}
                onChange={formik.handleChange}
                label="Method"
              >
                {PAYMENT_METHOD_OPTIONS.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              name="collectedAt"
              label="Date collected"
              type="date"
              value={formik.values.collectedAt}
              onChange={formik.handleChange}
              InputLabelProps={{ shrink: true }}
              error={formik.touched.collectedAt && Boolean(formik.errors.collectedAt)}
              helperText={formik.touched.collectedAt && (formik.errors.collectedAt as string)}
            />
            <TextField
              fullWidth
              size="small"
              name="referenceNumber"
              label="Reference number (optional)"
              placeholder="Cheque no, txn id, receipt no"
              value={formik.values.referenceNumber}
              onChange={formik.handleChange}
            />
            <TextField
              fullWidth
              size="small"
              name="notes"
              label="Notes (optional)"
              multiline
              minRows={2}
              value={formik.values.notes}
              onChange={formik.handleChange}
            />
          </Stack>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>Cancel</Button>
        <Button
          type="submit"
          form="record-payment-form"
          variant="contained"
          color="success"
          disabled={submitting || outstanding === 0}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Payments />}
          sx={{ borderRadius: 2 }}
        >
          {submitting ? 'Recording…' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordPaymentDialog;
